import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shield, Users, Check, X as XIcon } from "lucide-react";
import PricingTable from "@/components/tuition/PricingTable";

export default function Tuition() {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const selectedProgram = selected !== null ? t.tuition.programs[selected] : null;

  return (
    <section
      id="tuition"
      data-testid="tuition-section"
      className="py-24 sm:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-[2px] bg-[#FF3B30]" />
          <span className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] font-body">
            {t.tuition.label}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight text-white">
            {t.tuition.title}
          </h2>
          <span
            data-testid="coming-soon-badge"
            className="inline-block bg-[#FF3B30] text-white font-heading text-xs uppercase tracking-[0.2em] px-4 py-1.5"
          >
            {t.tuition.badge}
          </span>
        </div>

        <p className="text-sm sm:text-base text-[#A3A3A3] font-body leading-relaxed max-w-2xl mb-4">
          {t.tuition.subtitle}
        </p>

        <div className="flex items-center gap-2 mb-12">
          <Users className="w-4 h-4 text-[#FF3B30]" />
          <span className="text-sm font-heading uppercase tracking-wider text-[#FF3B30]">
            {t.tuition.limited}
          </span>
        </div>

        <PricingTable programs={t.tuition.programs} table={t.tuition.table} onSelect={setSelected} />

        <p className="text-xs text-[#555] font-body mb-8">{t.tuition.raceNote}</p>

        <div className="flex items-start gap-3 bg-[#111111] border border-[#262626] p-5 mb-8" data-testid="tuition-disclaimer">
          <Shield className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" />
          <p className="text-xs text-[#A3A3A3] font-body leading-relaxed">
            {t.tuition.disclaimer}
          </p>
        </div>

        <button
          data-testid="tuition-apply-btn"
          onClick={() => scrollTo("forms")}
          className="bg-[#FF3B30] text-white font-heading uppercase tracking-widest px-8 py-4 text-sm hover:bg-[#FF5C53] transition-all hover:-translate-y-0.5"
        >
          {t.tuition.cta}
        </button>
      </div>

      {/* Detail Dialog */}
      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent
          className="bg-[#111111] border-[#262626] text-white max-w-lg rounded-none sm:rounded-none"
          data-testid="tuition-detail-dialog"
        >
          {selectedProgram && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl sm:text-3xl uppercase tracking-wider text-white">
                  {selectedProgram.program}
                </DialogTitle>
                <DialogDescription className="text-sm text-[#A3A3A3] font-body">
                  {selectedProgram.duration} — {selectedProgram.price}
                </DialogDescription>
              </DialogHeader>

              <div className="mt-4">
                <h4 className="font-heading text-sm uppercase tracking-[0.15em] text-[#FF3B30] mb-4">
                  What&apos;s included
                </h4>
                <ul className="space-y-2.5">
                  {selectedProgram.details.map((item) => (
                    <li key={item} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-[#32D74B] shrink-0 mt-0.5" />
                      <span className="text-sm text-[#A3A3A3] font-body">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 pt-4 border-t border-[#262626]">
                <div className="flex items-start gap-2.5">
                  <XIcon className="w-4 h-4 text-[#FF453A] shrink-0 mt-0.5" />
                  <span className="text-sm text-[#555] font-body">{selectedProgram.notIncluded}</span>
                </div>
              </div>

              <button
                data-testid="tuition-dialog-apply-btn"
                onClick={() => {
                  setSelected(null);
                  setTimeout(() => scrollTo("forms"), 300);
                }}
                className="w-full mt-6 bg-[#FF3B30] text-white font-heading uppercase tracking-widest px-8 py-4 text-sm hover:bg-[#FF5C53] transition-all hover:-translate-y-0.5"
              >
                {t.tuition.cta}
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
